<script setup>
import { computed } from 'vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconOpenExternal from '@/components/icon/IconOpenExternal.vue'
import StreamSourceDropdown from '@/components/ui/StreamSourceDropdown.vue'

/**
 * Destination card in the Settings panel: the livestream this production
 * pushes to. "Change" opens the stream picker right in the card; locked while
 * the production is online.
 */
const props = defineProps({
	destinationStream: { type: Object, default: null },
	disabledStreamIds: { type: Array, default: () => [] },
	/** `hasLiveSubscription` from the store: `{ enabled }` or false. */
	hasLiveSubscription: { type: [Object, Boolean], default: false },
	enabled: { type: Boolean, default: true },
	locked: { type: Boolean, default: false },
	/** Picker open (v-model:editing). */
	editing: { type: Boolean, default: false },
})
const emit = defineEmits(['update:editing', 'set-destination', 'remove-destination', 'open-stream'])

const pickerDisabled = computed(() => !props.enabled || props.locked || !!(props.hasLiveSubscription && !props.hasLiveSubscription.enabled))
const choose = (stream) => {
	emit('set-destination', stream)
	emit('update:editing', false)
}
</script>

<template>
	<section class="flex flex-col gap-3 rounded-xl bg-surface-1 p-4" data-testid="settings-destination">
		<div class="flex items-center justify-between gap-2">
			<div class="min-w-0">
				<h3 class="m-0 text-sm font-semibold text-surface-text-2">Destination</h3>
				<p class="m-0 mt-0.5 truncate text-xs text-surface-8" data-testid="settings-destination-name">{{ destinationStream?.name || 'No destination yet' }}</p>
			</div>
			<Tooltip v-if="!editing" :active="locked" content="Locked while live" wrapper-class="inline-flex" wrap-trigger>
				<FormButton type="secondary" size="sm" :disabled="pickerDisabled" data-testid="settings-change-destination" @click="emit('update:editing', true)">
					{{ destinationStream ? 'Change' : 'Choose' }}
				</FormButton>
			</Tooltip>
		</div>

		<div v-if="editing" class="flex flex-col gap-2" data-testid="settings-destination-picker">
			<StreamSourceDropdown
				:disabled="pickerDisabled"
				:dropdown-disabled="pickerDisabled"
				:selected-stream="destinationStream"
				:disabled-streams="disabledStreamIds"
				:drop-right="false"
				:show-playlist="false"
				source="dynamic"
				:ref-link="0"
				:only-live-selector="true"
				placeholder="Choose a livestream"
				@on-source-selected="choose"
			/>
			<div class="flex items-center justify-between gap-2">
				<FormButton v-if="destinationStream" type="link" size="sm" class="!px-0 text-xs text-surface-8 hover:text-error-light" :disabled="pickerDisabled" data-testid="settings-destination-unlink" @click="emit('remove-destination'); emit('update:editing', false)">Unlink</FormButton>
				<span v-else />
				<FormButton type="secondary" size="sm" data-testid="settings-destination-done" @click="emit('update:editing', false)">Done</FormButton>
			</div>
		</div>
		<p v-else-if="locked" class="m-0 text-xs text-surface-7">Stop the production to change it.</p>

		<FormButton v-if="destinationStream && !editing" type="link" size="sm" class="!px-0 self-start text-xs text-surface-8" @click="emit('open-stream')">
			<icon-base width="12" height="12" class="mr-1.5"><icon-open-external /></icon-base>Open stream page
		</FormButton>
	</section>
</template>
